<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import type { Actor, Case, Dentist, HealthCenter } from "@/types";
import { filesPathName } from "@/router";
import { referenceTo } from "@/types";
import Dossier from "./DossierDisplay.vue";
import { computed, inject } from "vue";

import { filesKey } from "@/logic/provide";
import { useRouter } from "vue-router";

export type ActorFilter = {
  withCases: boolean;
};

export interface Props {
  entity: Dentist | Actor | HealthCenter | null;
  panelX: number;
}

const props = defineProps<Props>();

const router = useRouter();
const cases = inject(filesKey);

// Current entity's related cases
const currentCasesSelection = computed(() => {
  if (props.entity === null || !cases) {
    return null;
  }

  const source = props.entity.cases;
  return source
    .map((id) => {
      const key = cases.value.index.get(id);
      if (key === undefined) {
        return undefined;
      } else {
        return cases.value.list[key];
      }
    })
    .filter((a) => a !== undefined) as Array<Case>;
});

const redirectToCreation = () => {
  if (!props.entity) {
    return;
  }
  router.push({
    name: filesPathName,
    query: {
      requester: props.entity.id,
    },
  });
};

// Current entity's informations

const entityName = computed(() => {
  if (!props.entity) {
    return "la selection courante";
  }
  return referenceTo(props.entity).name;
});
</script>

<template>
  <div class="case-list" :style="{ 'padding-top': panelX + 'px' }">
    <div class="sidecar" v-if="currentCasesSelection">
      <div class="header-container">
        <h2>
          Dossier{{ currentCasesSelection.length > 1 ? "s" : "" }} lié{{
            currentCasesSelection.length > 1 ? "s" : ""
          }}
        </h2>
      </div>
      <div class="container-cases">
        <div>
          <span class="button" @click.stop="redirectToCreation"
            >Créer un nouveau dossier avec {{ entityName }} comme
            requérant</span
          >
        </div>
        <div class="container-listing" @click.stop>
          <span v-if="currentCasesSelection.length === 0">Aucun dossier</span>
          <template v-for="dossier in currentCasesSelection" :key="dossier.id">
            <Dossier openMode="InlineList" :modelValue="dossier" />
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.case-list {
  padding-left: 2em;
}

.container-cases {
  display: flex;
  flex-direction: column;
  padding-top: 1em;
  margin-left: 1em;

  .container-stats {
    text-align: right;
  }
}

.container-listing {
  display: flex;
  flex-direction: column;
  padding-top: 1em;
  row-gap: 4em;
  margin-bottom: 100px;
}

.header-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 56px;
  min-width: 680px;

  border-bottom-width: 6px;
  border-bottom-style: solid;
  border-color: var(--vt-c-purple);
  white-space: nowrap;

  h2 {
    color: var(--vt-c-purple);
  }
}
</style>
